<!-- src/components/EmailVerificationBanner.vue -->
<template>
  <q-banner
    inline-actions
    class="bg-orange-1 text-orange-10 rounded-borders-12 q-ma-md border-orange"
    style="z-index: 9999"
    dir="rtl"
  >
    <template v-slot:avatar>
      <q-icon name="mdi-email-alert-outline" color="orange-10" size="sm" />
    </template>

    <div class="text-weight-bold">بريدك الإلكتروني غير موثق!</div>
    <div class="text-caption opacity-80">يرجى التحقق من صندوق الوارد الخاص بك وتفعيل حسابك لتتمكن من استخدام كافة الميزات.</div>

    <template v-slot:action>
      <q-btn
        flat
        rounded
        label="لقد قمت بالتحقق"
        @click="checkVerificationStatus"
        :loading="isChecking"
        class="text-bold"
      />
      <q-btn
        unelevated
        rounded
        color="orange-10"
        label="إعادة إرسال الرابط"
        @click="resendVerificationEmail"
        :loading="isResending"
        class="q-ml-sm text-bold"
      />
    </template>
  </q-banner>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { useQuasar } from "quasar";
import { sendEmailVerification } from "firebase/auth";
import { auth } from "../config/firebase";
import { useAuth } from "../composables/useAuth";

const $q = useQuasar();
const isResending = ref(false);
const isChecking = ref(false);
let autoCheckInterval = null;

const { forceUserUpdate } = useAuth();

const checkVerificationStatus = async (isAuto = false) => {
  if (isAuto) console.log("[FCM Observer] 🤖 Checking verification status automatically...");
  if (!isAuto) isChecking.value = true;
  
  try {
    await forceUserUpdate();
    const isVerified = auth.currentUser?.emailVerified;
    console.log(`[FCM Observer] Status: ${isVerified ? '✅ VERIFIED' : '❌ NOT VERIFIED'}`);

    if (isVerified) {
      console.log("[FCM Observer] Success! Stopping interval.");
      if (autoCheckInterval) clearInterval(autoCheckInterval);
      
      if (!isAuto) {
        $q.notify({
          type: "positive",
          message: "تم توثيق البريد بنجاح! شكراً لك.",
          icon: "check_circle",
        });
      }
    } else if (!isAuto) {
      $q.notify({
        type: "warning",
        message: "البريد ما زال غير موثق، يرجى الضغط على الرابط المرسل إليك.",
        icon: "info",
      });
    }
  } catch (error) {
    console.error("[FCM Observer] Error during check:", error);
    if (!isAuto) {
      $q.notify({
        type: "negative",
        message: "فشل تحديث الحالة، يرجى المحاولة لاحقاً.",
        icon: "report_problem",
      });
    }
  } finally {
    if (!isAuto) isChecking.value = false;
  }
};

onMounted(() => {
  console.log("[FCM Observer] 🛰️ Component mounted. Starting 30s polling...");
  // 🤖 التحقق التلقائي كل 30 ثانية لتسهيل التجربة على المستخدم
  autoCheckInterval = setInterval(() => {
    if (auth.currentUser && !auth.currentUser.emailVerified) {
      checkVerificationStatus(true);
    } else {
      console.log("[FCM Observer] Polling skipped: User already verified or not logged in.");
    }
  }, 30000); // 30 ثانية
});

onUnmounted(() => {
  if (autoCheckInterval) clearInterval(autoCheckInterval);
});

const resendVerificationEmail = async () => {
  if (!auth.currentUser) return;
  isResending.value = true;
  try {
    await sendEmailVerification(auth.currentUser);
    $q.notify({
      type: "positive",
      message: "تم إرسال رابط توثيق جديد إلى بريدك الإلكتروني!",
      icon: "check_circle",
    });
  } catch (error) {
    let message = "حدث خطأ، يرجى المحاولة لاحقاً.";
    if (error.code === "auth/too-many-requests") {
      message = "لقد طلبت هذا الرابط كثيراً، يرجى الانتظار قليلاً قبل المحاولة مجدداً.";
    }
    $q.notify({
      type: "negative",
      message: message,
      icon: "report_problem",
    });
    console.error("Error resending verification email:", error);
  } finally {
    isResending.value = false;
  }
};
</script>

<style scoped>
.border-orange {
  border: 1px solid rgba(255, 152, 0, 0.3);
}
.rounded-borders-12 {
  border-radius: 12px;
}
</style>
